import React, { useCallback, useRef } from 'react';
import { ColDef } from 'ag-grid-community';
import { ShopsModel } from '../../models/Shops';
import { AgGridTable, AgGridTableRef } from '@shared/components/ui/AgGridTable';
import { useAgGridColumns } from '../../hooks/useAgGridColumns';
import {
  ShopSettingsModal,
  ShopSettingsModalRef,
} from '../modals/ShopSettingsModal';

interface ShopsAgGridTableProps {
  shops: ShopsModel[];
  isLoading?: boolean;
  isFetching?: boolean; // 🎯 Añadir isFetching para control de opacidad
  className?: string;
  refetch?: () => void;
  // 🎯 Props para ColumnSelector
  selectedColumns?: string[];
  // 🎯 Props para infinite scroll
  fetchNextPage?: () => void;
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  // 🎯 Props para sorting personalizado
  sorting?: { field: string; direction: 'asc' | 'desc' } | null;
  onSortingChange?: (
    sorting: { field: string; direction: 'asc' | 'desc' } | null
  ) => void;
}

export const ShopsAgGridTable = React.memo(function ShopsAgGridTable({
  shops,
  isFetching = false, // 🎯 Añadir isFetching para control de opacidad
  refetch,
  selectedColumns = [],
  fetchNextPage,
  hasNextPage,
  isFetchingNextPage,
  sorting,
  onSortingChange,
}: ShopsAgGridTableProps) {
  const agGridRef = useRef<AgGridTableRef>(null);

  // 🎯 El sorting ahora se maneja automáticamente por AgGridTable
  // No necesitamos lógica manual aquí

  // 🎯 Evitar crash: handler no-op para onGridReady
  const handleGridReady = useCallback(() => {}, []);

  // Estado y referencia para ShopSettingsModal
  const settingsModalRef = useRef<ShopSettingsModalRef>(null);

  // Handler para abrir el modal de configuración
  const handleOpenSettings = useCallback((shop: ShopsModel) => {
    if (settingsModalRef.current) {
      // Pasar el objeto completo de la tienda con todos sus datos
      settingsModalRef.current.open(shop);
    }
  }, []);

  // Manejar cambio de estado de la tienda
  const handleToggleStatus = useCallback(
    (shop: ShopsModel) => {
      // TODO: Implementar lógica para cambiar estado de la tienda
      // shop.id, shop.status se usarán aquí
      refetch?.();
    },
    [refetch]
  );

  // Manejar eliminación de la tienda
  const handleDeleteShop = useCallback(
    (shop: ShopsModel) => {
      // 🎯 La eliminación real se hace en DropdownActions
      // Aquí solo actualizamos la lista después de la eliminación exitosa
      refetch?.();
    },
    [refetch]
  );

  // 🎯 Verificar si alguna tienda trae la propiedad `team` desde la API
  const hasTeamData = React.useMemo(() => {
    return Array.isArray(shops)
      ? shops.some((shop: any) =>
          Object.prototype.hasOwnProperty.call(shop, 'team')
        )
      : false;
  }, [shops]);

  // 🎯 Usar el hook para manejar las columnas (ahora con cellRenderers incluidos)
  const { filteredColumnDefs } = useAgGridColumns({
    hasTeamData,
    selectedColumns,
    onOpenSettings: handleOpenSettings,
    onToggleStatus: handleToggleStatus,
    onDelete: handleDeleteShop,
    refetch,
  });

  // 🎯 Las columnas ya vienen con sus cellRenderers desde el hook
  // Ya no necesitamos aplicar renderers aquí, todo está en useAgGridColumns.tsx
  const columnDefsWithRenderers: ColDef[] = filteredColumnDefs;

  return (
    <>
      <div
        className={`w-full ${isFetching && !isFetchingNextPage ? 'opacity-50 pointer-events-none' : ''}`}
      >
        <AgGridTable
          ref={agGridRef}
          data={shops}
          columnDefs={columnDefsWithRenderers}
          height="calc(100vh - 150px)"
          enableSelection={false} // 🎯 Shops NO tiene selección múltiple
          onGridReady={handleGridReady}
          className="rounded-lg shadow-sm"
          fetchNextPage={fetchNextPage}
          hasNextPage={hasNextPage}
          isFetchingNextPage={isFetchingNextPage}
          gridOptions={{
            // 🎯 Configuración estándar sin forzar anchos
            columnDefs: columnDefsWithRenderers,
            suppressColumnVirtualisation: true, // Evitar virtualización de columnas
            suppressRowVirtualisation: false,   // Mantener virtualización de filas
          }}
          sorting={sorting}
          onSortingChange={onSortingChange}
        />
      </div>
      <ShopSettingsModal ref={settingsModalRef} />
    </>
  );
});
